import { db, storage } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Banner, BannerAttachment } from '@/domain/types/collections';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import {
  checkAndDeactivateIfNeeded,
  reorderActiveBanners,
} from '@/domain/use-cases/manage-banners';
import BannerAttachments from '@/presentation/components/banner-attachments';
import BannerImageUpload from '@/presentation/components/banner-image-upload';
import Spinner from '@/presentation/components/spinner';
import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { useEffect, useState } from 'react';
import { FiArrowLeft, FiSave, FiTrash2 } from 'react-icons/fi';
import { MdWarning } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const BannerDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logUpdate, logDelete } = useAdminLogger();

  const [banner, setBanner] = useState<Banner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    titleEN: '',
    subtitle: '',
    subtitleEN: '',
    description: '',
    descriptionEN: '',
    isActive: true,
    order: 0,
  });

  const [newBannerImage, setNewBannerImage] = useState<File | null>(null);
  const [newBannerImageEN, setNewBannerImageEN] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<BannerAttachment[]>([]);
  const [attachmentsEN, setAttachmentsEN] = useState<BannerAttachment[]>([]);

  useEffect(() => {
    const fetchBanner = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, DOCUMENTS_DB.BANNERS, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() } as Banner;
          setBanner(data);
          setFormData({
            title: data.title || '',
            titleEN: data.titleEN || '',
            subtitle: data.subtitle || '',
            subtitleEN: data.subtitleEN || '',
            description: data.description || '',
            descriptionEN: data.descriptionEN || '',
            isActive: data.isActive,
            order: data.order || 0,
          });
          setAttachments(data.attachments || []);
          setAttachmentsEN(data.attachmentsEN || []);
        } else {
          toast.error('Banner no encontrado', { theme: 'colored' });
          navigate('/dashboard/banners');
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        toast.error('Error al cargar el banner', { theme: 'colored' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanner();
  }, [id, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleActiveToggle = () => {
    setFormData((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  const handleUpdate = async () => {
    if (!id || !banner) return;

    if (!formData.title.trim()) {
      toast.error('El título (ES) es requerido', { theme: 'colored' });
      return;
    }
    if (formData.title.length > 40) {
      toast.error('El título (ES) no puede exceder 40 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.titleEN.trim()) {
      toast.error('El título (EN) es requerido', { theme: 'colored' });
      return;
    }
    if (formData.titleEN.length > 40) {
      toast.error('El título (EN) no puede exceder 40 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (formData.subtitle && formData.subtitle.length > 20) {
      toast.error('El subtítulo (ES) no puede exceder 20 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (formData.subtitleEN && formData.subtitleEN.length > 20) {
      toast.error('El subtítulo (EN) no puede exceder 20 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La descripción (ES) es requerida', { theme: 'colored' });
      return;
    }
    if (formData.description.length > 90) {
      toast.error('La descripción (ES) no puede exceder 90 caracteres', {
        theme: 'colored',
      });
      return;
    }
    if (!formData.descriptionEN.trim()) {
      toast.error('La descripción (EN) es requerida', { theme: 'colored' });
      return;
    }
    if (formData.descriptionEN.length > 90) {
      toast.error('La descripción (EN) no puede exceder 90 caracteres', {
        theme: 'colored',
      });
      return;
    }

    // Validar order si el banner está activo
    if (formData.isActive && (formData.order < 1 || formData.order > 3)) {
      toast.error('El orden debe estar entre 1 y 3 para banners activos', {
        theme: 'colored',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Si se va a activar y estaba inactivo, verificar límite
      if (formData.isActive && !banner.isActive) {
        const result = await checkAndDeactivateIfNeeded();
        if (result.deactivated) {
          toast.info('Se desactivó automáticamente el banner más antiguo', {
            theme: 'colored',
            autoClose: 5000,
          });
        }
      }

      let imageUrl = banner.imageUrl;
      let imageUrlEN = banner.imageUrlEN;

      // Si hay una nueva imagen (ES), subirla
      if (newBannerImage) {
        toast.info('Subiendo nueva imagen (ES)...');

        // Eliminar imagen anterior si existe
        if (banner.imageUrl && banner.imageUrl.includes('firebase')) {
          try {
            const oldImageRef = ref(storage, banner.imageUrl);
            await deleteObject(oldImageRef);
          } catch (e) {
            console.warn('No se pudo eliminar la imagen anterior (ES):', e);
          }
        }

        const imageFileName = `${Date.now()}_${newBannerImage.name}`;
        const imageRef = ref(storage, `banners/images/${imageFileName}`);
        const uploadTask = uploadBytesResumable(imageRef, newBannerImage);

        imageUrl = await new Promise<string>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            null,
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            },
          );
        });
      }

      // Si hay una nueva imagen (EN), subirla
      if (newBannerImageEN) {
        toast.info('Subiendo nueva imagen (EN)...');

        // Eliminar imagen EN anterior si existe
        if (banner.imageUrlEN && banner.imageUrlEN.includes('firebase')) {
          try {
            const oldImageENRef = ref(storage, banner.imageUrlEN);
            await deleteObject(oldImageENRef);
          } catch (e) {
            console.warn('No se pudo eliminar la imagen anterior (EN):', e);
          }
        }

        const imageFileNameEN = `${Date.now()}_en_${newBannerImageEN.name}`;
        const imageRefEN = ref(storage, `banners/images/${imageFileNameEN}`);
        const uploadTaskEN = uploadBytesResumable(imageRefEN, newBannerImageEN);

        imageUrlEN = await new Promise<string>((resolve, reject) => {
          uploadTaskEN.on(
            'state_changed',
            null,
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTaskEN.snapshot.ref);
              resolve(url);
            },
          );
        });
      }

      // Actualizar documento
      const docRef = doc(db, DOCUMENTS_DB.BANNERS, id);

      await updateDoc(docRef, {
        title: formData.title,
        titleEN: formData.titleEN,
        subtitle: formData.subtitle || undefined,
        subtitleEN: formData.subtitleEN || undefined,
        description: formData.description,
        descriptionEN: formData.descriptionEN,
        imageUrl: imageUrl,
        imageUrlEN: imageUrlEN,
        attachments: attachments,
        attachmentsEN: attachmentsEN,
        isActive: formData.isActive,
        order: formData.isActive ? formData.order : 0,
        updatedAt: serverTimestamp(),
      });

      // No llamamos a reorderActiveBanners() aquí porque el usuario puede haber establecido
      // manualmente el orden. Solo se reordena automáticamente al crear nuevos banners.

      // Log the action
      await logUpdate(DOCUMENTS_DB.BANNERS, id, formData.title, {
        title: formData.title,
        description: formData.description,
        isActive: formData.isActive,
      });

      toast.success('Banner actualizado correctamente', { theme: 'colored' });

      // Recargar datos
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        setBanner({ id: updatedDoc.id, ...updatedDoc.data() } as Banner);
      }
    } catch (error) {
      console.error('Error updating banner:', error);
      toast.error('Error al actualizar el banner', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !banner) return;

    setIsSubmitting(true);

    try {
      // Eliminar imagen del storage
      if (banner.imageUrl && banner.imageUrl.includes('firebase')) {
        try {
          const imageRef = ref(storage, banner.imageUrl);
          await deleteObject(imageRef);
        } catch (e) {
          console.warn('No se pudo eliminar la imagen:', e);
        }
      }

      // Eliminar adjuntos tipo file del storage (ES)
      if (banner.attachments && banner.attachments.length > 0) {
        for (const attachment of banner.attachments) {
          if (
            attachment.type === 'file' &&
            attachment.url.includes('firebase')
          ) {
            try {
              const fileRef = ref(storage, attachment.url);
              await deleteObject(fileRef);
            } catch (e) {
              console.warn('No se pudo eliminar adjunto (ES):', e);
            }
          }
        }
      }

      // Eliminar adjuntos tipo file del storage (EN)
      if (banner.attachmentsEN && banner.attachmentsEN.length > 0) {
        for (const attachment of banner.attachmentsEN) {
          if (
            attachment.type === 'file' &&
            attachment.url.includes('firebase')
          ) {
            try {
              const fileRef = ref(storage, attachment.url);
              await deleteObject(fileRef);
            } catch (e) {
              console.warn('No se pudo eliminar adjunto (EN):', e);
            }
          }
        }
      }

      // Eliminar documento
      const docRef = doc(db, DOCUMENTS_DB.BANNERS, id);
      await deleteDoc(docRef);

      // Reordenar banners activos
      await reorderActiveBanners();

      // Log the action
      await logDelete(DOCUMENTS_DB.BANNERS, id, banner.title);

      toast.success('Banner eliminado correctamente', { theme: 'colored' });
      navigate('/dashboard/banners');
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Error al eliminar el banner', { theme: 'colored' });
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!banner) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Banner no encontrado
        </div>
      </div>
    );
  }

  const remainingTitleChars = 40 - formData.title.length;
  const remainingTitleENChars = 40 - formData.titleEN.length;
  const remainingSubtitleChars = 20 - formData.subtitle.length;
  const remainingSubtitleENChars = 20 - (formData.subtitleEN?.length ?? 0);
  const remainingDescChars = 90 - formData.description.length;
  const remainingDescENChars = 90 - formData.descriptionEN.length;

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/banners')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Editar Banner</h1>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSubmitting}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiTrash2 />
            Eliminar
          </button>

          <button
            onClick={handleUpdate}
            disabled={isSubmitting}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave />
            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Confirmar Eliminación</h2>
            <p className="text-gray-600 mb-6">
              ¿Está seguro de que desea eliminar el banner "{banner.title}"?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulario (similar a add) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-xl font-semibold">Información Básica</h2>

            {/* Títulos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Título *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  maxLength={40}
                  placeholder="Título en español (máx. 40 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p
                  className={`text-xs mt-1 ${remainingTitleChars < 10 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingTitleChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Title *
                </label>
                <input
                  type="text"
                  name="titleEN"
                  value={formData.titleEN}
                  onChange={handleInputChange}
                  maxLength={40}
                  placeholder="Title in English (max. 40 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
                />
                <p
                  className={`text-xs mt-1 ${remainingTitleENChars < 10 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingTitleENChars} characters remaining
                </p>
              </div>
            </div>

            {/* Subtítulos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Subtítulo
                </label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  maxLength={20}
                  placeholder="Subtítulo opcional (máx. 20 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p
                  className={`text-xs mt-1 ${remainingSubtitleChars < 5 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingSubtitleChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Subtitle
                </label>
                <input
                  type="text"
                  name="subtitleEN"
                  value={formData.subtitleEN}
                  onChange={handleInputChange}
                  maxLength={20}
                  placeholder="Optional subtitle (max. 20 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
                />
                <p
                  className={`text-xs mt-1 ${remainingSubtitleENChars < 5 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingSubtitleENChars} characters remaining
                </p>
              </div>
            </div>

            {/* Descripciones */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    ES
                  </span>
                  Descripción breve *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={90}
                  rows={3}
                  placeholder="Descripción en español (máx. 90 caracteres)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
                <p
                  className={`text-xs mt-1 ${remainingDescChars < 20 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingDescChars} caracteres restantes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded mr-1">
                    EN
                  </span>
                  Brief description *
                </label>
                <textarea
                  name="descriptionEN"
                  value={formData.descriptionEN}
                  onChange={handleInputChange}
                  maxLength={90}
                  rows={3}
                  placeholder="Description in English (max. 90 chars)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500 resize-none"
                />
                <p
                  className={`text-xs mt-1 ${remainingDescENChars < 20 ? 'text-red-500' : 'text-gray-500'}`}
                >
                  {remainingDescENChars} characters remaining
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Imágenes del Banner</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    ES
                  </span>
                  Imagen en Español
                </h3>
                <BannerImageUpload
                  onFileChange={setNewBannerImage}
                  imageUrl={banner.imageUrl}
                  inputId="banner-image-upload-es"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    EN
                  </span>
                  Image in English
                </h3>
                <BannerImageUpload
                  onFileChange={setNewBannerImageEN}
                  imageUrl={banner.imageUrlEN}
                  inputId="banner-image-upload-en"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Adjuntos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    ES
                  </span>
                  Adjunto en Español
                </h3>
                <BannerAttachments
                  attachments={attachments}
                  onChange={setAttachments}
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <span className="inline-block bg-gray-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                    EN
                  </span>
                  Attachment in English
                </h3>
                <BannerAttachments
                  attachments={attachmentsEN}
                  onChange={setAttachmentsEN}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Estado</h2>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">Publicar banner</p>
                <p className="text-xs text-gray-600">
                  {formData.isActive ? 'Activo' : 'Inactivo'}
                </p>
              </div>
              <button
                onClick={handleActiveToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isActive ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {formData.isActive && !banner.isActive && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <MdWarning className="text-yellow-600 text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Activando banner
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Si ya hay 3 activos, se desactivará el más antiguo.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Campo de orden */}
          {formData.isActive && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Orden de Prioridad</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posición (1-3)
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  min={1}
                  max={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-2">
                  1 = Primera posición, 2 = Segunda, 3 = Tercera
                </p>
              </div>
            </div>
          )}

          {banner.createdAt && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Creado:</span>{' '}
                {new Date(banner.createdAt.seconds! * 1000).toLocaleString(
                  'es-ES',
                )}
              </p>
              {banner.updatedAt && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Actualizado:</span>{' '}
                  {new Date(banner.updatedAt.seconds! * 1000).toLocaleString(
                    'es-ES',
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerDetailsPage;
